/**
 *  Replaces all line breaks in a string with a single space.
 *  This is useful for normalizing text input where line breaks may not be desired.
 *  @param text - The input string to be normalized.
 *  @param options - Optional behavior flags.
 *  @param options.preserveBr - When true, keeps `<br>` tags intact (only whitespace/newline chars are collapsed). Use for HTML content where authors meant the breaks to render.
 *  @returns A new string with all line breaks replaced by a single space.
 */
export function normalizeLineBreaks(text: string, options?: { preserveBr?: boolean }): string {
  const lineBreakRegex = options?.preserveBr
    ? /[\r\n]+|\\n/gi
    : /<br\s*\/?>|[\r\n]+|\\n/gi
  let normalized = text
    .replace(lineBreakRegex, ' ')
    .replace(/\s+/g, ' ')
    .trim()

  // Combine opening tag space insertions
  const openTagRegex = /([\p{L}\p{N}])(<em>|<i>|<a[^>]*>)/gu
  normalized = normalized.replace(openTagRegex, '$1 $2')

  // Combine closing tag space insertions
  const closeTagRegex = /(<\/em>|<\/i>|<\/a>)([\p{L}\p{N}])/gu
  normalized = normalized.replace(closeTagRegex, '$1 $2')

  // Insert spaces between adjacent closing and opening tags
  const adjacentTagRegex = /(<\/em>|<\/i>|<\/a>)(<em>|<i>|<a[^>]*>)/gu
  normalized = normalized.replace(adjacentTagRegex, '$1 $2')

  // Clean up any multiple spaces introduced
  normalized = normalized.replace(/\s+/g, ' ')

  // Remove spaces before common punctuation marks
  normalized = normalized.replace(/\s+([.,:!?])/g, '$1')

  return normalized
}

/**
 * Cleans up HTML attributes that have escaped quotes from Ibexa/Solr.
 * Converts things like class="\&quot;Normal\&quot;" to class="Normal"
 * @param text - HTML string to clean
 * @returns HTML with cleaned attribute values
 */
export function normalizeHtmlAttributes(text: string): string {
  // Handle attributes with quotes: attr="value"
  let result = text.replace(/(\w+)="([^"]*)"/g, (match, attrName, attrValue) => {
    // Remove escaped quotes and HTML entities from attribute values
    const cleaned = attrValue.replace(/\\&quot;|&quot;|\\"|"/g, '').trim()
    return `${attrName}="${cleaned}"`
  })

  // Handle attributes with escaped quotes: attr=\"value\"
  result = result.replace(/(\w+)=\\"([^\\]*)\\"/g, (match, attrName, attrValue) => {
    // Remove escaped quotes and HTML entities from attribute values
    const cleaned = attrValue.replace(/\\&quot;|&quot;|\\"|"/g, '').trim()
    return `${attrName}="${cleaned}"`
  })

  return result
}
